import { useCallback, useEffect, useState, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { addMonths, endOfMonth, startOfMonth } from 'lib/datetime/dateUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import GeoContext from 'contexts/geoContext'
import type { Moment } from 'moment'
import moment from 'moment'
import FlightsDatePickerDay from './FlightsDatePickerDay'
import { FlightsFareTypes } from 'constants/flight'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchFlightCalendar } from 'actions/FlightActions'
import { fireInteractionEvent } from 'api/googleTagManager'
import { flightsArrivalDateSelect, flightsDepartureDateSelect } from 'analytics/eventDefinitions'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import { StandaloneFlightMenu } from 'contexts/Flights/FlightSearchWidget/types'
import type { FlightSearchWidgetFlightItem } from 'contexts/Flights/FlightSearchWidget/types'
import type { DatePickerMonthChangeHandler } from 'components/Common/Calendar/DatePicker'
import DatePicker from 'components/Common/Calendar/DatePicker'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import { getFlightCalendarKey } from 'lib/flights/flightUtils'
import { arrayToMap, groupBy, min } from 'lib/array/arrayUtils'
import truncateText from 'lib/string/truncateText'
import { AGENT_HUB_MIN_CALENDAR_START_MONTH, getAgentHubFlightEnabledWeekdays } from 'lib/agentHub/flights'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'

interface Props {
  minDate?: Date
  maxDate?: Date
  className?: string
  type: 'single' | 'dual'
  id: string
  onDateChange?: (date: Date) => void
  featuredRanges?: Array<{
    start: Date
    end: Date
  }>
  required?: boolean
}

function FlightsDatePicker(props: Props) {
  const {
    minDate,
    maxDate,
    className,
    type,
    id,
    onDateChange,
    featuredRanges,
    required,
  } = props

  const intl = useIntl()
  const state = useContext(FlightSearchWidgetStateContext)

  const prevFlight = useMemo(() => {
    const prevFlightIndex = state.flights.findIndex((flight) => flight.id === id)

    if (prevFlightIndex !== -1) {
      return state.flights[prevFlightIndex - 1]
    }
  }, [state.flights, id])

  const currentFlight = useMemo(() => {
    return state.flights.find((flight) => flight.id === id) || state.flights[0]
  }, [state.flights, id])

  const { fareType, fareCabin } = state

  const {
    checkinDate: startDate,
    checkoutDate: endDate,
    departureAirport,
    arrivalAirport,
  } = currentFlight

  const getFlightName = useCallback((airport?: App.AirportLocation) => {
    return truncateText(airport?.cityDesc || airport?.airportName || '', 6, '', '..')
  }, [])

  const getFlightLabel = useMemo(() => {
    return (
      dayTime: number,
      startDate: Moment | undefined,
      currentFlight: FlightSearchWidgetFlightItem,
      selectedFlightsByDate: Record<number, FlightSearchWidgetFlightItem>,
      isMultiCity: boolean,
    ): string => {
      const isDeparting = startDate?.valueOf() === dayTime
      const departingAirportName = getFlightName(currentFlight.departureAirport)
      const arrivalAirportName = getFlightName(currentFlight.arrivalAirport)
      const defaultLabel = isDeparting ? departingAirportName : arrivalAirportName
      const multiCityLabel = getFlightName(selectedFlightsByDate[dayTime]?.departureAirport)
      return isMultiCity ? multiCityLabel : defaultLabel
    }
  }, [getFlightName])

  const searchDispatch = useContext(FlightSearchWidgetDispatchContext)

  const { currentRegionCode, currentCurrency } = useContext(GeoContext)

  const isMultiCity = fareType?.value === FlightsFareTypes.MULTI_CITY

  const isReturn = fareType?.value === FlightsFareTypes.RETURN
  const isOneWay = fareType?.value === FlightsFareTypes.ONE_WAY
  const tripType = isOneWay ? 'OneWay' as const : 'Return' as const

  const [date, setDate] = useState<Date>(startDate?.toDate() ?? new Date())
  const startDateQuery = useMemo(() => moment(startOfMonth(date)).format(ISO_DATE_FORMAT), [date])
  const endDateQuery = useMemo(() => moment(endOfMonth(addMonths(date, 1))).format(ISO_DATE_FORMAT), [date])

  const fareClass = fareCabin?.value

  const cacheKey = getFlightCalendarKey({
    startDate: startDateQuery,
    endDate: endDateQuery,
    origin: departureAirport?.airportCode ?? '',
    destination: arrivalAirport?.airportCode ?? '',
    region: currentRegionCode,
    currency: currentCurrency,
    tripType,
    fareClass,
  })

  const flightCalendar = useAppSelector(state => state.flights.flightCalendar[cacheKey])
  const loading = useAppSelector(state => state.flights.flightCalendarLoading[cacheKey])

  const earliestBookingDate = !loading && flightCalendar?.earliestBookingDate ? new Date(flightCalendar.earliestBookingDate + 'T00:00:00') : minDate
  const finalBookingDate = !loading && flightCalendar?.finalBookingDate ? new Date(flightCalendar.finalBookingDate) : maxDate

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (departureAirport && arrivalAirport && !isMultiCity && !isOneWay && !state.disableCalendarPricing) {
      dispatch(fetchFlightCalendar({
        startDate: startDateQuery,
        endDate: endDateQuery,
        origin: departureAirport.airportCode,
        destination: arrivalAirport.airportCode,
        tripType,
        fareClass,
      }))
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, state.disableCalendarPricing])

  const { selectingDateType, flights: stateFlights } = state

  const onDayClick = useCallback((selectedDay: Date) => {
    const day = moment(selectedDay)

    if ([FlightsFareTypes.ONE_WAY, FlightsFareTypes.MULTI_CITY].includes(fareType?.value as FlightsFareTypes)) {
      const selectedDayCachedPrice = flightCalendar?.cachedPrices?.[day.format(ISO_DATE_FORMAT)]?.cost ?? 0
      const selectedDayPrice = Math.floor(selectedDayCachedPrice / 2)
      searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, id, date: day, quotedPrice: isOneWay ? selectedDayPrice : undefined })
      fireInteractionEvent(flightsDepartureDateSelect())
      searchDispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, activeMenu: undefined })

      // Clear next flights dates
      const currentFlightIndex = stateFlights.findIndex(flight => flight.id === id)
      for (let i = currentFlightIndex + 1; i < stateFlights.length; i++) {
        const flight = stateFlights[i]
        searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, id: flight.id, date: null })
      }
    } else {
      const selectedDayCachedPrice = flightCalendar?.cachedPrices?.[day.format(ISO_DATE_FORMAT)]?.cost ?? 0
      const selectedDayPrice = Math.floor(selectedDayCachedPrice / 2)

      // User explicitly clicked the "Returning" field and selected a valid return date
      if (selectingDateType === 'returning' && startDate && day.isSameOrAfter(startDate)) {
        searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, id, date: day, quotedPrice: selectedDayPrice })
        fireInteractionEvent(flightsArrivalDateSelect())
        searchDispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, id, activeMenu: undefined })
      // User explicitly clicked the "Departing" field - reset departure and clear return
      } else if (selectingDateType === 'departing') {
        searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, id, date: day, quotedPrice: selectedDayPrice })
        // Clear return date if it's now before the new departure date
        if (endDate && day.isAfter(endDate)) {
          searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, id, date: null, quotedPrice: null })
        }
        fireInteractionEvent(flightsDepartureDateSelect())
        // Move to selecting return date
        searchDispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, id, activeMenu: StandaloneFlightMenu.date, selectingDateType: 'returning' })
      // Fallback: original behavior for when selectingDateType is not set
      } else if (startDate && !endDate && day.isSameOrAfter(startDate)) {
        const departingCachedPrice = flightCalendar?.cachedPrices?.[startDate.format(ISO_DATE_FORMAT)]?.cost ?? 0
        const departingPrice = Math.floor(departingCachedPrice / 2)
        searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, id, date: startDate, quotedPrice: departingPrice })
        searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, id, date: day, quotedPrice: selectedDayPrice })
        fireInteractionEvent(flightsArrivalDateSelect())
        searchDispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, id, activeMenu: undefined })
      } else {
        searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, id, date: day, quotedPrice: selectedDayPrice })
        searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, id, date: null, quotedPrice: null })
        fireInteractionEvent(flightsDepartureDateSelect())
      }
    }

    onDateChange?.(date)
  }, [fareType?.value, isOneWay, onDateChange, date, searchDispatch, id, startDate, endDate, stateFlights, selectingDateType, flightCalendar])

  const onMonthChange = useCallback<DatePickerMonthChangeHandler>((direction, date) => {
    setDate(date)
  }, [])

  const cheapestPricesByMonth = useMemo(() => {
    const pricesByMonth = groupBy(
      Object.entries(flightCalendar?.cachedPrices ?? {}),
      ([date]) => moment(date).format('YYYY-MM'),
      ([, value]) => value,
    )

    // now narrow it down to the cheapest for said month
    return arrayToMap(
      Array.from(pricesByMonth.entries()),
      ([monthYear]) => monthYear,
      ([, values]) => min(values, val => val.cost)?.cost,
    )
  }, [flightCalendar])

  const defaultMonth = useMemo(() => {
    if (prevFlight?.checkinDate) {
      return moment(prevFlight.checkinDate).get('month')
    }

    if (isAgentHubEnabled && (!startDate || startDate.isBefore(moment().month(AGENT_HUB_MIN_CALENDAR_START_MONTH)))) {
      return AGENT_HUB_MIN_CALENDAR_START_MONTH
    }

    return date.getMonth()
  }, [date, prevFlight, startDate])

  const defaultYear = useMemo(() => {
    if (prevFlight?.checkinDate) {
      return moment(prevFlight.checkinDate).get('year')
    }

    return date.getFullYear()
  }, [date, prevFlight])

  const selectedFlightsByDate: Record<number, FlightSearchWidgetFlightItem> = useMemo(() => {
    return state.flights.reduce((dates, flight) => {
      if (flight.checkinDate) {
        return {
          ...dates,
          [flight.checkinDate.valueOf()]: flight,
        }
      }
      return dates
    }, {})
  }, [state.flights])

  const selectDateError = intl.formatMessage({
    id: 'Y4HjF2MhbDw_YSiUkNEu',
    defaultMessage: 'Please select a date',
    description: 'Error message when a flight date is not selected in the date picker',
  })

  return (
    <div className={className}>
      <HiddenInput
        name="startDate"
        value={startDate?.format(ISO_DATE_FORMAT)}
        required={required}
        invalidMessage={selectDateError}
      />
      <HiddenInput
        name="endDate"
        value={endDate?.format(ISO_DATE_FORMAT)}
        required={isReturn && required}
        invalidMessage={selectDateError}
      />

      <DatePicker
        min={earliestBookingDate}
        max={finalBookingDate}
        onMonthChange={onMonthChange}
        defaultMonth={defaultMonth}
        defaultYear={defaultYear}
        type={type}
        dayRender={(day: Date, params) => {
          const { disabled } = params
          const dayTime = day.getTime()
          const dateOfDay = moment(day)
          const monthKey = dateOfDay.format('YYYY-MM')
          const selected = (startDate?.valueOf() === dayTime || endDate?.valueOf() === dayTime) || (isMultiCity && Boolean(selectedFlightsByDate[dayTime]))
          const cachedPrice = !isMultiCity ? flightCalendar?.cachedPrices?.[dateOfDay.format(ISO_DATE_FORMAT)]?.cost ?? 0 : 0
          const isInRange = (!selected && startDate && endDate) ? dateOfDay.isBetween(startDate, endDate) : false
          const perPersonPrice = Math.floor(cachedPrice / 2)
          // if multicity, we want to be able to book a date on the same day or later
          const shouldCheckDate = prevFlight && fareType?.value === FlightsFareTypes.MULTI_CITY
          let isAfterLastFlight = shouldCheckDate ? dateOfDay.isAfter(prevFlight.checkinDate) || dateOfDay.isSame(prevFlight.checkinDate) : true
          if (shouldCheckDate) {
            isAfterLastFlight = dateOfDay.isAfter(prevFlight.checkinDate)
          }
          const isFeatured = !isAgentHubEnabled && featuredRanges?.some(range => dateOfDay.isBetween(range.start, range.end, 'days', '[]'))

          const label = getFlightLabel(
            dayTime,
            startDate,
            currentFlight,
            selectedFlightsByDate,
            isMultiCity,
          )

          const enabledWeekdays = getAgentHubFlightEnabledWeekdays(selectingDateType === 'departing' ? departureAirport : arrivalAirport)
          const isDisabledWeekday = !enabledWeekdays.includes(dateOfDay.day())
          const shouldDisableWeekday = isDisabledWeekday && isAgentHubEnabled

          return <FlightsDatePickerDay
            key={dayTime}
            day={day}
            data-testid={`day-${dateOfDay.format('YYYY-MM-DD')}`}
            selected={selected}
            disabled={disabled || !isAfterLastFlight || shouldDisableWeekday}
            price={!state.disableCalendarPricing ? perPersonPrice : undefined}
            loading={loading}
            isInRange={isInRange}
            onClick={onDayClick}
            cheapest={cheapestPricesByMonth.get(monthKey) === cachedPrice}
            featured={isFeatured}
            label={selected ? label : undefined}
          />
        }}
      />
    </div>
  )
}

export default FlightsDatePicker
